import { ConstraintModel, GroupNode } from '../compile/compilePattern'
import { Disjunction, Equality, GapConstraint, OrderEdge, Provenance } from '../types'
import { GapResolution, resolveGaps } from './gaps'
import { GraphResult, buildGraph } from './orderGraph'

const MAX_SUBSETS = 512
const MAX_GENERATED = 20000
const MAX_COMBOS = 64

export interface SelectionResult {
  ok: boolean
  conflict?: { provenance: Provenance[] }
  unverified: boolean
  chosen: {
    conditionIds: string[]
    droppedConditionIds: string[]
    /** option index per active disjunction, aligned with activeDisjunctions order */
    disjunctionPicks: number[]
  }
  graph?: GraphResult
  gapResolution?: GapResolution
}

/**
 * Lazy k-subset generator — never materializes the full combination space.
 * The eager version froze the main thread on all-AND unsatisfiable patterns
 * (2^n − 1 subsets generated with zero attempts at ~20 conditions).
 */
function* lazyCombinations<T>(items: T[], size: number, start = 0): Generator<T[]> {
  if (size === 0) {
    yield []
    return
  }
  for (let i = start; i <= items.length - size; i += 1) {
    for (const rest of lazyCombinations(items, size - 1, i + 1)) {
      yield [items[i], ...rest]
    }
  }
}

/** Every top-level group must meet its count with the remaining conditions. */
const isDropFeasible = (groupTree: GroupNode[], dropped: Set<string>): boolean => {
  const isMet = (node: GroupNode): boolean => {
    let met = 0
    node.members.forEach((member) => {
      if (member.kind === 'condition') {
        if (!dropped.has(member.conditionId)) met += 1
      } else if (isMet(member.node)) {
        met += 1
      }
    })
    return met >= node.count
  }
  return groupTree.every(isMet)
}

/**
 * Exhaustive-within-bounds N-of-M selection + disjunction search (spec §7.3):
 * try satisfying ALL conditions first, then search droppable subsets in
 * increasing size — complete where greedy dropping is not. Every candidate
 * runs the order graph and gap resolution; the first success wins. On total
 * failure the ALL-conditions conflict is reported (most informative).
 */
export const solveSelection = (model: ConstraintModel): SelectionResult => {
  const isActiveProvenance = (provenance: Provenance, active: Set<string>): boolean =>
    provenance.conditionId === undefined || active.has(provenance.conditionId)

  const attempt = (
    active: Set<string>
  ): {
    success?: { graph: GraphResult; gapResolution: GapResolution; picks: number[] }
    conflict?: { provenance: Provenance[] }
    unverified: boolean
  } => {
    const edges: OrderEdge[] = model.edges.filter((edge) =>
      isActiveProvenance(edge.provenance, active)
    )
    const equalities: Equality[] = model.equalities.filter((equality) =>
      isActiveProvenance(equality.provenance, active)
    )
    const gaps: GapConstraint[] = model.gaps.filter((gap) =>
      isActiveProvenance(gap.provenance, active)
    )
    const activeDisjunctions: Disjunction[] = model.disjunctions.filter((disjunction) =>
      isActiveProvenance(disjunction.provenance, active)
    )

    const comboCount = activeDisjunctions.reduce(
      (product, disjunction) => product * Math.max(1, disjunction.options.length),
      1
    )
    const combosToTry = Math.min(comboCount, MAX_COMBOS)
    const combosCapped = comboCount > MAX_COMBOS

    // Window-anchor picks feed prerequisite ordering: option index == bars back.
    const windowIndexByCondition = new Map<string, number>()
    activeDisjunctions.forEach((disjunction, i) => {
      if (disjunction.label === 'window' && disjunction.provenance.conditionId) {
        windowIndexByCondition.set(disjunction.provenance.conditionId, i)
      }
    })
    const relevantPrereqs = model.prereqEdges.filter(
      (prereq) => active.has(prereq.before) && active.has(prereq.after)
    )

    let firstConflict: { provenance: Provenance[] } | undefined
    let sawUnverified = combosCapped

    for (let combo = 0; combo < combosToTry; combo += 1) {
      const picks: number[] = []
      let remainder = combo
      for (const disjunction of activeDisjunctions) {
        const optionCount = Math.max(1, disjunction.options.length)
        picks.push(remainder % optionCount)
        remainder = Math.floor(remainder / optionCount)
      }

      const backOf = (conditionId: string): number => {
        const index = windowIndexByCondition.get(conditionId)
        return index === undefined ? 0 : picks[index]
      }
      const prereqsOrdered = relevantPrereqs.every(
        (prereq) => backOf(prereq.before) >= backOf(prereq.after)
      )
      if (!prereqsOrdered) continue

      const comboEdges = [...edges]
      const comboEqualities = [...equalities]
      const comboGaps = [...gaps]
      activeDisjunctions.forEach((disjunction, i) => {
        const option = disjunction.options[picks[i]]
        if (!option) return
        comboEdges.push(...option.edges)
        comboEqualities.push(...option.equalities)
        comboGaps.push(...option.gaps)
      })

      const graph = buildGraph(comboEdges, comboEqualities, model.intervals)
      if (!graph.ok) {
        firstConflict = firstConflict ?? { provenance: graph.conflict?.provenance ?? [] }
        continue
      }
      const gapResolution = resolveGaps(comboGaps, graph)
      if (!gapResolution.ok) {
        firstConflict = firstConflict ?? { provenance: gapResolution.conflict?.provenance ?? [] }
        continue
      }
      if (gapResolution.unverified) sawUnverified = true
      return { success: { graph, gapResolution, picks }, unverified: sawUnverified }
    }

    return { conflict: firstConflict, unverified: sawUnverified }
  }

  const allIds = model.conditionIds
  const allActive = new Set(allIds)
  const firstAttempt = attempt(allActive)
  if (firstAttempt.success) {
    return {
      ok: true,
      unverified: firstAttempt.unverified,
      chosen: {
        conditionIds: allIds,
        droppedConditionIds: [],
        disjunctionPicks: firstAttempt.success.picks,
      },
      graph: firstAttempt.success.graph,
      gapResolution: firstAttempt.success.gapResolution,
    }
  }

  // Only conditions with ancestor-group slack can ever be dropped: a condition
  // whose singleton drop is already infeasible can never appear in a feasible
  // subset either (removing more conditions only lowers group counts further).
  // In the default all-AND shape this set is EMPTY, so the search below is
  // skipped entirely instead of enumerating 2^n infeasible subsets.
  const droppable = allIds.filter((id) => isDropFeasible(model.groupTree, new Set([id])))

  // Order drop candidates so conflict members are tried first — an ordering
  // heuristic only; completeness comes from exhausting subset sizes.
  const conflictIds = new Set(
    (firstAttempt.conflict?.provenance ?? [])
      .map((provenance) => provenance.conditionId)
      .filter((id): id is string => id !== undefined)
  )
  const candidates = [
    ...droppable.filter((id) => conflictIds.has(id)),
    ...droppable.filter((id) => !conflictIds.has(id)),
  ]

  let subsetsGenerated = 0
  let subsetsTried = 0
  let subsetsCapped = false
  let sawUnverified = firstAttempt.unverified

  outer: for (let size = 1; size <= candidates.length; size += 1) {
    for (const subset of lazyCombinations(candidates, size)) {
      // EVERY generated subset counts toward the cap — infeasible ones
      // included — so the bound holds even when nothing is feasible.
      subsetsGenerated += 1
      if (subsetsTried >= MAX_SUBSETS || subsetsGenerated >= MAX_GENERATED) {
        subsetsCapped = true
        break outer
      }
      const dropped = new Set(subset)
      if (!isDropFeasible(model.groupTree, dropped)) continue
      subsetsTried += 1
      const active = new Set(allIds.filter((id) => !dropped.has(id)))
      const result = attempt(active)
      if (result.unverified) sawUnverified = true
      if (result.success) {
        return {
          ok: true,
          unverified: sawUnverified,
          chosen: {
            conditionIds: allIds.filter((id) => !dropped.has(id)),
            droppedConditionIds: subset,
            disjunctionPicks: result.success.picks,
          },
          graph: result.success.graph,
          gapResolution: result.success.gapResolution,
        }
      }
    }
  }

  return {
    ok: false,
    unverified: sawUnverified || subsetsCapped,
    conflict: firstAttempt.conflict ?? { provenance: [] },
    chosen: { conditionIds: [], droppedConditionIds: [], disjunctionPicks: [] },
  }
}
